/**
 * admin/src/pages/SettingsPage.tsx
 * Cài đặt hệ thống Admin v3.0
 * - Cache Management
 * - System Info
 * - Admin Accounts display
 */

import { useState } from 'react';
import { adminFetch } from '../hooks/useAdmin';
import { useStats } from '../hooks/useStats';
import { toast } from '../components/ui/index';

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div className="card card-lg" style={{ marginBottom: 20 }}>
      <h3 style={{ margin: '0 0 20px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 16, fontWeight: 700 }}>
        <span style={{ fontSize: 20 }}>{icon}</span>
        {title}
      </h3>
      {children}
    </div>
  );
}

function SettingRow({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 0',
      borderBottom: '1px solid var(--border-soft)',
      gap: 24,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-1)', marginBottom: 3 }}>{label}</div>
        {description && <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.5 }}>{description}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{children}</div>
    </div>
  );
}

// ─── Cache Section ─────────────────────────────────────────────────────────────

type CacheState = 'idle' | 'loading' | 'success' | 'error';

function CacheSection() {
  const [state, setState] = useState<CacheState>('idle');
  const [result, setResult] = useState<{ deleted: number } | null>(null);

  async function handleClear() {
    if (state === 'loading') return;
    setState('loading');
    try {
      const res = await adminFetch<{ ok: boolean; deleted: number }>('/cache/invalidate', { method: 'POST' });
      setState(res.ok ? 'success' : 'error');
      setResult(res.ok ? { deleted: res.deleted } : null);
      if (res.ok) toast(`Đã xóa ${res.deleted} cache key`, 'success');
    } catch {
      setState('error');
      toast('Lỗi xóa cache', 'error');
    }
    setTimeout(() => { setState('idle'); setResult(null); }, 5000);
  }

  return (
    <Section title="Cache Management" icon="⚡">
      <SettingRow
        label="KV Cache"
        description="Xóa toàn bộ Cloudflare KV cache. Dashboard và API sẽ tải lại dữ liệu mới từ D1 database. Thao tác này an toàn — dữ liệu không bị mất."
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {result && (
            <span style={{ fontSize: 13, color: 'var(--success)', fontWeight: 600 }}>
              ✓ Xóa {result.deleted} key
            </span>
          )}
          <button
            onClick={handleClear}
            disabled={state === 'loading'}
            className={`btn btn-sm ${state === 'success' ? 'btn-secondary' : state === 'error' ? 'btn-danger' : 'btn-secondary'}`}
            style={state === 'success' ? { borderColor: 'var(--success)', color: 'var(--success)' } : undefined}
          >
            {state === 'loading' ? '⏳ Đang xóa…' : state === 'success' ? '✓ Đã xóa' : '🗑 Xóa KV Cache'}
          </button>
        </div>
      </SettingRow>
      <SettingRow
        label="Cache Strategy"
        description="Cache TTL hiện tại: 300s cho API, 3600s cho static content."
      >
        <div className="badge badge-info">Cloudflare KV</div>
      </SettingRow>
    </Section>
  );
}

// ─── System Info ──────────────────────────────────────────────────────────────

function SystemInfoSection() {
  const { stats } = useStats();
  const env = import.meta.env.DEV ? 'Development' : 'Production';

  const infos = [
    { label: 'Environment', value: env, badge: env === 'Production' ? 'success' : 'warning' },
    { label: 'Database', value: 'Cloudflare D1 (SQLite)', badge: 'info' },
    { label: 'Storage', value: 'Cloudflare R2', badge: 'info' },
    { label: 'Edge Runtime', value: 'Cloudflare Workers', badge: 'info' },
    { label: 'Frontend', value: 'React + Vite + TS', badge: 'info' },
    { label: 'Admin Dashboard', value: 'v3.0 — Light/Dark Theme', badge: 'premium' },
  ];

  return (
    <Section title="Thông tin hệ thống" icon="🖥️">
      {infos.map(({ label, value, badge }) => (
        <SettingRow key={label} label={label}>
          <span className={`badge badge-${badge}`}>{value}</span>
        </SettingRow>
      ))}
      {stats && (
        <>
          <SettingRow label="Tổng users" description="Tài khoản phụ huynh">
            <strong style={{ color: 'var(--text-1)', fontSize: 18, fontWeight: 800 }}>
              {stats.users.total.toLocaleString('vi-VN')}
            </strong>
          </SettingRow>
          <SettingRow label="Tổng học sinh">
            <strong style={{ color: 'var(--text-1)', fontSize: 18, fontWeight: 800 }}>
              {stats.students.total.toLocaleString('vi-VN')}
            </strong>
          </SettingRow>
          <SettingRow label="Phân bổ tier">
            <div style={{ display: 'flex', gap: 8 }}>
              <span className="badge badge-free">Free: {stats.users.free}</span>
              <span className="badge badge-basic">Member: {stats.users.basic}</span>
              <span className="badge badge-premium">Premium: {stats.users.premium}</span>
            </div>
          </SettingRow>
        </>
      )}
    </Section>
  );
}

// ─── Display Preferences ──────────────────────────────────────────────────────

function DisplaySection() {
  const [theme, setTheme] = useState(() => localStorage.getItem('admin-theme') ?? 'dark');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('admin-sidebar-collapsed') === 'true');

  function handleThemeChange(t: string) {
    setTheme(t);
    localStorage.setItem('admin-theme', t);
    document.documentElement.setAttribute('data-theme', t);
  }

  function handleSidebarToggle() {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    localStorage.setItem('admin-sidebar-collapsed', String(next));
    if (next) document.documentElement.setAttribute('data-sidebar', 'collapsed');
    else document.documentElement.removeAttribute('data-sidebar');
  }

  return (
    <Section title="Hiển thị" icon="🎨">
      <SettingRow label="Giao diện (Theme)" description="Dark mode tối ưu cho làm việc lâu dài. Light mode sáng như Notion.">
        <div style={{ display: 'flex', gap: 8 }}>
          {['dark', 'light'].map(t => (
            <button
              key={t}
              onClick={() => handleThemeChange(t)}
              className={`btn btn-sm ${theme === t ? 'btn-primary' : 'btn-secondary'}`}
            >
              {t === 'dark' ? '🌙 Dark' : '☀️ Light'}
            </button>
          ))}
        </div>
      </SettingRow>
      <SettingRow label="Sidebar" description="Thu gọn sidebar thành icon-only mode để có thêm không gian làm việc.">
        <button onClick={handleSidebarToggle} className="btn btn-sm btn-secondary">
          {sidebarCollapsed ? '→ Mở rộng Sidebar' : '← Thu gọn Sidebar'}
        </button>
      </SettingRow>
    </Section>
  );
}

// ─── Danger Zone ──────────────────────────────────────────────────────────────

function DangerZone() {
  const [confirm, setConfirm] = useState('');
  return (
    <Section title="Danger Zone" icon="⚠️">
      <div style={{
        padding: '16px 20px',
        background: 'var(--danger-soft)',
        border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: 'var(--radius-lg)',
      }}>
        <div style={{ fontWeight: 600, color: 'var(--danger)', marginBottom: 8, fontSize: 14 }}>
          Rebuild toàn bộ blog index
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
          Xóa và tái tạo lại blog index từ tất cả file markdown trên R2. Thời gian: ~30-60s.
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            className="input"
            placeholder='Gõ "rebuild" để xác nhận'
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            style={{ maxWidth: 220, fontSize: 13 }}
          />
          <button
            className="btn btn-sm btn-danger"
            disabled={confirm !== 'rebuild'}
            onClick={() => {
              toast('Tính năng này cần gọi API rebuild — liên hệ dev', 'warning');
              setConfirm('');
            }}
          >
            🔄 Rebuild Index
          </button>
        </div>
      </div>
    </Section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SettingsPage() {
  return (
    <div>
      <div className="page-header">
        <div className="page-header__left">
          <h1>Settings</h1>
          <p>Cài đặt và thông tin hệ thống Admin</p>
        </div>
      </div>

      <CacheSection />
      <DisplaySection />
      <SystemInfoSection />
      <DangerZone />
    </div>
  );
}
